import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { toast } from "sonner";

export interface ProductSubsection {
  id: number;
  descricao?: string;
  referencia?: string;
  isInativo: boolean;
}

export interface ProductSubsectionFormData {
  descricao?: string;
  referencia?: string;
  isInativo?: boolean;
}

export function useProductSubsections() {
  const queryClient = useQueryClient();

  const { data: subsections = [], isLoading } = useQuery({
    queryKey: ["product-subsections"],
    queryFn: async () => {
      const { data } = await apiClient.get<ProductSubsection[]>("/api/subsecoes-produto");
      return data;
    },
  });

  const createSubsection = useMutation({
    mutationFn: async (formData: ProductSubsectionFormData) => {
      const { data } = await apiClient.post<ProductSubsection>("/api/subsecoes-produto", formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subsections"] });
      toast.success("Subseção criada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao criar subseção: " + error.message);
    },
  });

  const updateSubsection = useMutation({
    mutationFn: async ({ id, ...formData }: ProductSubsectionFormData & { id: number }) => {
      const { data } = await apiClient.put<ProductSubsection>(`/api/subsecoes-produto/${id}`, formData);
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subsections"] });
      toast.success("Subseção atualizada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao atualizar subseção: " + error.message);
    },
  });

  const deleteSubsection = useMutation({
    mutationFn: async (id: number) => {
      await apiClient.delete(`/api/subsecoes-produto/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-subsections"] });
      toast.success("Subseção inativada com sucesso!");
    },
    onError: (error: Error) => {
      toast.error("Erro ao inativar subseção: " + error.message);
    },
  });

  return {
    subsections,
    isLoading,
    createSubsection,
    updateSubsection,
    deleteSubsection,
  };
}
